// pages/SearchPage.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  searchRestaurants,
  getClientSessionId,
  postAnalyticsImpressionsBatch,
} from "../api.js";
import RestaurantCard from "../components/RestaurantCard.jsx";
import FeedbackWidget from "../components/FeedbackWidget.jsx";

const PAGE_LIMIT = 25;
const KM_TO_MI = 1.609344;

/* --------------------------------------------
   Category options (canonical metas shown to users)
--------------------------------------------- */
const CATEGORY_OPTIONS = [
  "American",
  "Latin American",
  "European",
  "Middle Eastern",
  "African",
  "Indian",
  "South Asian",
  "Chinese",
  "Japanese",
  "Korean",
  "Southeast Asian",
  "Central Asian",
  "Oceanian",
  "Pacific",
  "Vegetarian",
  "Vegan",
  "Healthy",
  "Seafood",
  "Steakhouse",
  "Grill",
  "Barbecue",
  "Fast Food",
  "Fast Casual",
  "Café",
  "Coffee",
  "Tea",
  "Bakery",
  "Dessert",
  "Sweets",
  "Buffet",
  "All-You-Can-Eat",
  "Pub",
  "Bar",
  "Gastropub",
  "Fusion",
  "Contemporary",
  "Global",
  "Fine Dining",
  "Casual Dining",
  "Family Style",
  "Street Food",
  "Food Truck",
  "Specialty",
  "Themed",
  "Pop-Up",
].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

/* --------------------------------------------
   Split meta categories into component CSV for API
   e.g., ["Café / Coffee / Tea","Japanese"] -> "Café,Coffee,Tea,Japanese"
--------------------------------------------- */
function categoriesToCsv(ary) {
  if (!Array.isArray(ary) || ary.length === 0) return null;
  const parts = ary
    .flatMap((c) => String(c).split("/"))
    .map((s) => s.trim())
    .filter(Boolean);

  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(p);
    }
  }
  return out.length ? out.join(",") : null;
}

/* --------------------------------------------
   Stable id per search submit (clicks/impressions)
--------------------------------------------- */
function newSearchId() {
  try {
    if (typeof self !== "undefined" && self.crypto && typeof self.crypto.randomUUID === "function") {
      return self.crypto.randomUUID();
    }
  } catch {}
  return String(Date.now()) + "_" + Math.random().toString(36).slice(2);
}

export default function SearchPage() {
  /* ---------------- form controls ---------------- */
  const [location, setLocation] = useState("");
  const [radiusKm, setRadiusKm] = useState(2 * KM_TO_MI);
  const [orderby, setOrderby] = useState("distance");

  /* ---------------- categories ---------------- */
  const [categories, setCategories] = useState([]); // array of selected canonical metas
  const [catOpen, setCatOpen] = useState(false);

  /* ---------------- source selection (last interaction wins) ---------------- */
  const [sourceMode, setSourceMode] = useState(null); // 'location' | 'location' | null

  /* ---------------- browser location ---------------- */
  const [coords, setCoords] = useState(null); // { lat, lng } | null
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");

  /* ---------------- request/data state ---------------- */
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- paginated data ---------------- */
  const [results, setResults] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  /* ---------------- meta and plan ---------------- */
  const [meta, setMeta] = useState(null);
  const [plan, setPlan] = useState(null);

  /* ---------------- track last submitted params ---------------- */
  const [lastSearch, setLastSearch] = useState(null);

  /* ---------------- search session ids ---------------- */
  const clientSessionIdRef = useRef(getClientSessionId());
  const [searchId, setSearchId] = useState(null);

  /* ---------------- LOCATION job + notices ---------------- */
  const [locationJob, setLocationJob] = useState(null);
  const [hideLocationJobNotice, setHideLocationJobNotice] = useState(false);

  /* ---------------- abort handling ---------------- */
  const acRef = useRef(null);
  const abortInFlight = () => {
    try {
      acRef.current?.abort();
    } catch {}
  };
  function newAbortController() {
    // Shim for older Safari
    class NoopAbortController {
      constructor() {
        this.signal = undefined;
      }
      abort() {}
    }
    const AC =
      typeof window !== "undefined" && window.AbortController
        ? window.AbortController
        : NoopAbortController;
    abortInFlight();
    const ac = new AC();
    acRef.current = ac;
    return ac;
  }

  /* ---------------- keyboard detection (iOS visualViewport) ---------------- */
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    if (!window.visualViewport) return;
    const onResize = () => {
      const delta = window.innerHeight - window.visualViewport.height;
      setKeyboardOpen(delta > 80);
    };
    window.visualViewport.addEventListener("resize", onResize);
    onResize();
    return () => window.visualViewport.removeEventListener("resize", onResize);
  }, []);

  /* ---------------- infinite scroll sentinel ---------------- */
  const sentinelRef = useRef(null);

  /* ---------------- allow search if we have inputs ---------------- */
  const canSearch =
    (!!lastSearch || location.trim().length > 0 || !!coords) && !loading && !loadingMore;

  /* ---------------- analytics: impression queue ---------------- */
  const impressionQueueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const loggedThisMinuteRef = useRef(new Map());

  const metaForAnalytics = useMemo(() => {
    return {
      location: meta?.location ?? lastSearch?.location ?? null,
      radius: meta?.radius ?? lastSearch?.radius ?? Math.round(radiusKm * 1000),
      orderby: meta?.orderby ?? lastSearch?.orderby ?? orderby,
      center: meta?.center ?? null,
    };
  }, [meta, lastSearch, radiusKm, orderby]);

  function enqueueImpressions(rows) {
    if (!rows?.length) return;
    impressionQueueRef.current.push(...rows);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(flushImpressions, 800);
  }

  async function flushImpressions(forceBeacon = false) {
    const q = impressionQueueRef.current;
    impressionQueueRef.current = [];
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    if (!q.length) return;

    const payload = {
      client_session_id: clientSessionIdRef.current,
      search_id: searchId || null,
      plan_hint: plan || undefined,
      results: q,
    };

    try {
      if (forceBeacon) {
        const data = JSON.stringify(payload);
        const sent = (function () {
          try {
            if (typeof Blob !== "undefined") {
              const blob = new Blob([data], { type: "application/json" });
              return navigator.sendBeacon?.("/api/v1/analytics/impressions", blob);
            }
          } catch {}
          try {
            if (typeof TextEncoder !== "undefined") {
              const buf = new TextEncoder().encode(data);
              return navigator.sendBeacon?.("/api/v1/analytics/impressions", buf);
            }
          } catch {}
          try {
            return navigator.sendBeacon?.("/api/v1/analytics/impressions", data);
          } catch {}
          return false;
        })();
        if (!sent) {
          // no-op
        }
      } else {
        await postAnalyticsImpressionsBatch(payload);
      }
    } catch {
      // swallow
    }
  }

  useEffect(() => {
    const onHide = () => flushImpressions(true);
    const onUnload = () => flushImpressions(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);

  /* ---------------- visual highlight state ---------------- */
  const activeSource = useMemo(() => {
    if (sourceMode) return sourceMode;
    if (lastSearch?.location) return "location";
    if (Number.isFinite(lastSearch?.lat) && Number.isFinite(lastSearch?.lng))
      return "gps";
    return null;
  }, [sourceMode, lastSearch]);

  const locationActiveStyles =
    activeSource === "location"
      ? { borderColor: "var(--brand)", boxShadow: "0 0 0 2px rgba(37,99,235,0.25)" }
      : undefined;

  const gpsActiveStyles =
    activeSource === "gps"
      ? { borderColor: "var(--brand)", boxShadow: "0 0 0 2px rgba(37,99,235,0.25)" }
      : undefined;

  /* ---------------- click outside to close category popover ---------------- */
  useEffect(() => {
    if (!catOpen) return;
    const onDocClick = (e) => {
      const pop = document.querySelector(".multiselect-popover");
      const trigger = document.querySelector(".multiselect-trigger");
      if (!pop || !trigger) return;
      if (pop.contains(e.target) || trigger.contains(e.target)) return;
      setCatOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [catOpen]);

  /* ---------------- shared search runner ---------------- */
  const beginSearch = async (fixedParams) => {
    setError("");
    setLocationError("");
    setLoading(true);
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMeta(null);
    setLocationJob(null);
    setHideLocationJobNotice(false);
    setPlan(null);

    const sid = newSearchId();
    setSearchId(sid);
    setLastSearch(fixedParams);

    const ac = newAbortController();
    try {
      const base = {
        ...fixedParams,
        limit: PAGE_LIMIT,
        offset: 0,
        categories: categoriesToCsv(categories),
      };
      if (typeof AbortSignal !== "undefined" && ac?.signal instanceof AbortSignal) {
        base.signal = ac.signal;
      }

      const json = await searchRestaurants(base);

      const page = Array.isArray(json.results) ? json.results : [];
      setResults(page);

      const more =
        typeof json.has_more === "boolean" ? json.has_more : page.length === PAGE_LIMIT;
      setHasMore(more);
      setOffset(PAGE_LIMIT);

      setMeta({
        location: json.location ?? fixedParams.location ?? null,
        radius: json.radius ?? fixedParams.radius,
        center: json.center ?? null,
        orderby: json.orderby ?? fixedParams.orderby,
      });
      setLocationJob(json.location_job ?? null);
      setPlan(json.plan ?? null);

      const minuteIso = new Date();
      minuteIso.setSeconds(0, 0);
      const minuteKey = minuteIso.toISOString();

      const rows = page.map((r, i) => {
        const k = `${r.place_id}:${minuteKey}`;
        loggedThisMinuteRef.current.set(k, true);
        return {
          place_id: r.place_id,
          position: i + 1,
          location: json.location ?? fixedParams.location ?? null,
          radius_m: json.radius ?? fixedParams.radius,
          orderby: json.orderby ?? fixedParams.orderby,
          rating: r.rating ?? null,
          user_rating_count: r.user_rating_count ?? null,
          gf_safety_score: Number.isFinite(r.gf_safety_score) ? r.gf_safety_score : null,
          distance_m: r.distance_m ?? null,
          shown_at: new Date().toISOString(),
        };
      });
      enqueueImpressions(rows);
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- browser location helpers ---------------- */
  const isSecureContext =
    typeof window !== "undefined" && window.isSecureContext;

  async function useMyLocation() {
    setLocationError("");
    if (!("geolocation" in navigator)) {
      setLocationError("Geolocation is not available in this browser.");
      return;
    }
    if (!isSecureContext) {
      setLocationError("Geolocation requires HTTPS. Please use a secure connection.");
      return;
    }
    setLocating(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        try {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        } catch (err) {
          reject(err);
        }
      });
      const { latitude: lat, longitude: lng } = pos.coords || {};
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setCoords({ lat, lng });
        setSourceMode("gps");
        const fixedParams = {
          lat,
          lng,
          radius: Math.round(radiusKm * 1000),
          orderby,
        };
        await beginSearch(fixedParams);
      } else {
        setLocationError("Could not read your location coordinates.");
      }
    } catch (e) {
      const msg =
        e?.code === 1
          ? "Location permission denied."
          : e?.code === 2
          ? "Position unavailable."
          : e?.code === 3
          ? "Location request timed out."
          : e?.message || "Could not get your location.";
      setLocationError(msg);
    } finally {
      setLocating(false);
    }
  }

  /* ---------------- search: first page (manual click) ---------------- */
  const onSearchClick = async () => {
    const z = location.trim();

    const preferred =
      sourceMode ??
      (lastSearch?.location
        ? "location"
        : Number.isFinite(lastSearch?.lat) && Number.isFinite(lastSearch?.lng)
        ? "gps"
        : null);

    if (preferred === "location") {
      const zz = z || lastSearch?.location;
      if (zz) {
        await beginSearch({
          location: zz,
          radius: Math.round(radiusKm * 1000),
          orderby,
        });
        return;
      }
    }

    if (preferred === "gps") {
      const current =
        coords ||
        (lastSearch &&
        Number.isFinite(lastSearch.lat) &&
        Number.isFinite(lastSearch.lng)
          ? { lat: lastSearch.lat, lng: lastSearch.lng }
          : null);
      if (current) {
        await beginSearch({
          lat: current.lat,
          lng: current.lng,
          radius: Math.round(radiusKm * 1000),
          orderby,
        });
        return;
      }
    }

    if (z.length > 0) {
      await beginSearch({
        location: z,
        radius: Math.round(radiusKm * 1000),
        orderby,
      });
      return;
    }
    if (coords) {
      await beginSearch({
        lat: coords.lat,
        lng: coords.lng,
        radius: Math.round(radiusKm * 1000),
        orderby,
      });
      return;
    }
  };

  /* ---------------- pagination: load more ---------------- */
  const loadMore = async () => {
    if (!lastSearch || !hasMore || loading || loadingMore) return;

    setError("");
    setLoadingMore(true);

    const ac = newAbortController();
    try {
      const base = {
        ...lastSearch,
        limit: PAGE_LIMIT,
        offset,
        categories: categoriesToCsv(categories),
      };
      if (typeof AbortSignal !== "undefined" && ac?.signal instanceof AbortSignal) {
        base.signal = ac.signal;
      }

      const json = await searchRestaurants(base);

      const page = Array.isArray(json.results) ? json.results : [];
      setResults((prev) => prev.concat(page));

      const more =
        typeof json.has_more === "boolean" ? json.has_more : page.length === PAGE_LIMIT;

      setHasMore(more);
      setOffset((prev) => prev + page.length);

      if (json.location_job) setLocationJob(json.location_job);
      if (json.plan) setPlan(json.plan);

      const minuteIso = new Date();
      minuteIso.setSeconds(0, 0);
      const minuteKey = minuteIso.toISOString();

      const basePos = offset + 1;
      const rows = page
        .map((r, i) => {
          const k = `${r.place_id}:${minuteKey}`;
          if (loggedThisMinuteRef.current.get(k)) return null;
          loggedThisMinuteRef.current.set(k, true);
          return {
            place_id: r.place_id,
            position: basePos + i,
            location: meta?.location ?? lastSearch.location ?? null,
            radius_m: meta?.radius ?? lastSearch.radius,
            orderby: meta?.orderby ?? lastSearch.orderby,
            rating: r.rating ?? null,
            user_rating_count: r.user_rating_count ?? null,
            gf_safety_score: Number.isFinite(r.gf_safety_score) ? r.gf_safety_score : null,
            distance_m: r.distance_m ?? null,
            shown_at: new Date().toISOString(),
          };
        })
        .filter(Boolean);

      if (rows.length) enqueueImpressions(rows);
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || "Load more failed");
    } finally {
      setLoadingMore(false);
    }
  };

  /* ---------------- infinite scroll observer ---------------- */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !keyboardOpen) loadMore();
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [sentinelRef, hasMore, lastSearch, offset, loadingMore, keyboardOpen]);

  /* ---------------- cleanup on unmount ---------------- */
  useEffect(() => () => abortInFlight(), []);

  /* ---------------- optional: surface real errors on iOS ---------------- */
  useEffect(() => {
    const onErr = (e) =>
      console.log(
        "[window.onerror]",
        e.message,
        `${e.filename}:${e.lineno}:${e.colno}`,
        e.error && e.error.stack
      );
    const onRej = (e) => console.log("[unhandledrejection]", e.reason);
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  const count = results.length;

  /* ---------------- notices ---------------- */
  const showLocationJobNotice = !!locationJob && !hideLocationJobNotice && !locationJob.analyzed;
  const showFreePlanNotice = !!lastSearch && plan === "free";

  /* ---------------- shared analytics context ---------------- */
  const baseAnalyticsCtx = useMemo(() => {
    return {
      client_session_id: clientSessionIdRef.current,
      search_id: searchId || null,
      plan: plan || null,
      meta: {
        location: metaForAnalytics.location,
        radius: metaForAnalytics.radius,
        orderby: metaForAnalytics.orderby,
        center: metaForAnalytics.center || null,
      },
    };
  }, [searchId, plan, metaForAnalytics]);

  return (
    <div className="panel">
      {/* CONTROLS: single-line on desktop (5th cell is the Search button) */}
      <div
        className="controls"
        style={{
          display: "grid",
          gap: 12,
          alignItems: "end",
        }}
      >
        {/* LOCATION field with inline location button */}
        <div className="field">
          <label>Location or Zip/Postcode</label>
          <div className="location-input-row">
            <button
              type="button"
              className="loc-btn"
              onClick={useMyLocation}
              disabled={locating}
              aria-label="Use my current location"
              title="Use my current location"
              style={gpsActiveStyles}
            >
              {locating ? "…" : "📍"}
            </button>
            <input
              type="text"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setSourceMode("location");
              }}
              onFocus={() => setSourceMode("location")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSearch) {
                  e.preventDefault();
                  onSearchClick();
                }
              }}
              placeholder="94103"
              inputMode="numeric"
              autoComplete="postal-code"
              aria-label="location code"
              style={locationActiveStyles}
            />
          </div>
          {locationError && <div className="field-error">{locationError}</div>}
        </div>

        <div className="field">
          <label>Radius: {radiusKm / 1.609344} miles</label>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={radiusKm / 1.609344}
            onChange={(e) =>
              setRadiusKm(parseInt(e.target.value || "0", 10) * 1.609344)
            }
            aria-label="Search radius in miles"
          />
        </div>

        {/* Category filter */}
        <div className="field">
          <label>Categories</label>
          <div className="multiselect">
            <button
              type="button"
              className="multiselect-trigger"
              onClick={() => setCatOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={catOpen ? "true" : "false"}
            >
              {categories.length === 0 ? "All categories" : `${categories.length} selected`}
              <span className="caret" aria-hidden>▾</span>
            </button>

            {catOpen && (
              <div className="multiselect-popover" role="listbox" aria-multiselectable="true">
                <div className="multiselect-search">
                  <input
                    type="text"
                    placeholder="Filter…"
                    onChange={(e) => {
                      const q = e.target.value.toLowerCase().trim();
                      const items = Array.from(
                        document.querySelectorAll(".multiselect-option")
                      );
                      items.forEach((el) => {
                        const text = (el.getAttribute("data-label") || "").toLowerCase();
                        el.style.display = text.includes(q) ? "" : "none";
                      });
                    }}
                    aria-label="Filter categories"
                  />
                </div>
                <ul className="multiselect-list">
                  {CATEGORY_OPTIONS.map((label) => {
                    const selected = categories.includes(label);
                    return (
                      <li
                        key={label}
                        className="multiselect-option"
                        data-label={label}
                        role="option"
                        aria-selected={selected ? "true" : "false"}
                        onClick={() => {
                          setCategories((prev) =>
                            prev.includes(label)
                              ? prev.filter((x) => x !== label)
                              : [...prev, label]
                          );
                        }}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          tabIndex={-1}
                          checked={!!selected}
                        />
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="multiselect-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setCategories([])}
                    aria-label="Clear all categories"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setCatOpen(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="field">
          <label>Order by</label>
          <select
            value={orderby}
            onChange={(e) => setOrderby(e.target.value)}
            aria-label="Order results by"
          >
            <option value="distance">Distance</option>
            <option value="variety">Most Options</option>
            <option value="rating">Highest Rating</option>
          </select>
        </div>

        {/* Search button as 5th grid cell (same row) */}
        <div
          className="actions"
        >
          <button onClick={onSearchClick} disabled={!canSearch}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>
      </div>

      {/* Selected cuisines chips BELOW the row */}
      {categories.length > 0 && (
        <div className="chips chips-desktop" style={{ marginTop: 8 }} aria-live="polite">
          {categories.map((c) => (
            <span key={c} className="chip" title={c}>
              {c}
              <button
                type="button"
                className="chip-x"
                aria-label={`Remove ${c}`}
                onClick={() =>
                  setCategories((prev) => prev.filter((x) => x !== c))
                }
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}


      {!lastSearch && !location && !coords && (
        <div className="hint" style={{ marginTop: 8 }}>
          Start a search by clicking 📍 to use your location or by entering your zip/postcode.
        </div>
      )}
      {error && <div className="error">{error}</div>}

      {showLocationJobNotice && (
        <div
          className="notice"
          role="status"
          aria-live="polite"
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "#fff7e6",
            border: "1px solid #ffd591",
            color: "#8a5a00",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>We don't support this area yet.</strong> Want support sooner? Press feedback and let us know!
          </div>
          <button
            onClick={() => setHideLocationJobNotice(true)}
            aria-label="Dismiss notice"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {showFreePlanNotice && !showLocationJobNotice && (
        <div
          className="notice"
          role="status"
          aria-live="polite"
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "#e6f4ff",
            border: "1px solid #91caff",
            color: "#003a8c",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>Limited results available.</strong> Subscribe in account settings to see all results.
          </div>
        </div>
      )}

      {count > 0 && (
        <div className="summary" style={{ marginTop: 12 }}>
          Showing <strong>{count}</strong> result{count === 1 ? "" : "s"}
          {activeSource === "location" && (meta?.location || lastSearch?.location)
            ? ` for ${meta?.location || lastSearch?.location}`
            : ""}
          {activeSource === "location" && meta?.center ? ` near your location` : ""}{" "}
          within{" "}
          {(
            (meta?.radius ?? Math.round(radiusKm * 1000)) /
            (KM_TO_MI * 1000)
          ).toFixed(1)}{" "}
          miles
          {categories.length > 0 && (
            <>
              {" • "}
              <span className="badge">
                {categories.length} categor{categories.length === 1 ? "y" : "ies"}
              </span>
            </>
          )}
        </div>
      )}

      {loading && (
        <div className="skeletons">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton" />
          ))}
        </div>
      )}

      {!loading && count === 0 && lastSearch && !error && (
        <div className="hint">
          No gluten-friendly places found for this location and radius.
        </div>
      )}

      {count > 0 && (
        <ul className="grid">
          {results.map((r, idx) => (
            <RestaurantCard
              key={r.place_id}
              r={r}
              analyticsCtx={{
                ...baseAnalyticsCtx,
                position: idx + 1,
              }}
            />
          ))}
        </ul>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />

      {loadingMore && (
        <div className="hint" style={{ textAlign: "center", marginTop: 12 }}>
          Loading more…
        </div>
      )}
      {!loading && !loadingMore && hasMore && (
        <div className="actions" style={{ marginTop: 12 }}>
          <button onClick={loadMore}>Load more</button>
        </div>
      )}

      <FeedbackWidget />
    </div>
  );
}
